import React, { useState, useEffect, useMemo } from 'react';
import { X, Upload, FileText, Sparkles, Calendar, Clock, Pen, CheckCircle, Plus } from 'lucide-react';
import { AgreementDocument, uploadAgreementDocument } from '@/firebase/agreementService';
import { agreementCompletionService, AgreementCompletion } from '@/firebase/agreementCompletionService';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { locationService, Location } from '@/firebase/locationService';
import { vendorService, Vendor } from '@/firebase/vendorService';
import { employeeService, Employee } from '@/firebase/employeeService';
import { assetService, Asset, AssetType } from '@/firebase/assetService';
import { parseContract, ContractAnalysis } from '@/services/geminiService';
import VendorForm from './VendorForm';
import EmployeeForm from './EmployeeForm';
import FileSizeErrorModal from './FileSizeErrorModal';
import { trackEvidence } from '@/utils/analytics';

interface RunAgreementProps {
  agreement: AgreementDocument;
  onClose: () => void;
  onComplete: () => void;
  viewMode?: 'upload' | 'history'; // 'upload' for upload form, 'history' for history only
}

const RunAgreement: React.FC<RunAgreementProps> = ({ agreement, onClose, onComplete, viewMode = 'upload' }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [fileSizeError, setFileSizeError] = useState<{ fileName: string; fileSize: number } | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]); // Store all locations
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [signerName, setSignerName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [completions, setCompletions] = useState<AgreementCompletion[]>([]);
  const [selectedCompletion, setSelectedCompletion] = useState<AgreementCompletion | null>(null);
  const [runsLocationFilterId, setRunsLocationFilterId] = useState<string>('ALL');
  const [isEditingExpiry, setIsEditingExpiry] = useState(false);
  const [editedExpiryDate, setEditedExpiryDate] = useState<string>('');
  const [isUpdatingExpiry, setIsUpdatingExpiry] = useState(false);
  const [showAddVendorForm, setShowAddVendorForm] = useState(false);
  const [showAddEmployeeForm, setShowAddEmployeeForm] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [contractAnalysis, setContractAnalysis] = useState<ContractAnalysis | null>(null);
  const [manualExpiryDate, setManualExpiryDate] = useState<string>('');
  const [isAnalyzingDocument, setIsAnalyzingDocument] = useState(false);

  useEffect(() => {
    loadLocations();
    loadHistory();
    if (agreement.contractType === 'vendor_contract') {
      loadVendors();
    } else if (agreement.contractType === 'employee_contract') {
      loadEmployees();
    }
    // Load asset types for vendor contracts
    if (agreement.contractType === 'vendor_contract') {
      loadAssetTypes();
    }
    // Initialize field values based on contract type
    initializeFieldValues();
  }, [agreement.contractType]);

  // Check for existing contract analysis when contract name is entered (for vendor contracts)
  useEffect(() => {
    const checkExistingAnalysis = async () => {
      if (agreement.contractType === 'vendor_contract' && signerName.trim() && agreement.id && !contractAnalysis) {
        try {
          const existingResult = await agreementCompletionService.getCompletionsForAgreement(agreement.id);
          if (existingResult.success && existingResult.data) {
            // Find completion with matching signerName (contract name)
            const existingCompletion = existingResult.data.find(
              c => c.signerName?.trim().toLowerCase() === signerName.trim().toLowerCase()
            );
            
            if (existingCompletion && (existingCompletion.contractSummary || existingCompletion.expiryDate)) {
              // Use existing analysis
              const getDateValue = (date: any): Date | null => {
                if (!date) return null;
                if (date instanceof Date) return date;
                if (date?.toDate && typeof date.toDate === 'function') return date.toDate();
                if (typeof date === 'string') return new Date(date);
                if (typeof date === 'number') return new Date(date);
                return null;
              };
              
              // Reconstruct ContractAnalysis from existing completion
              const existingAnalysis: ContractAnalysis = {
                summary: existingCompletion.contractSummary || '',
                contractDate: getDateValue(existingCompletion.startDate || existingCompletion.contractDate),
                expiryDate: getDateValue(existingCompletion.endDate || existingCompletion.expiryDate),
                keyInformation: existingCompletion.contractKeyInformation || {}
              };
              
              setContractAnalysis(existingAnalysis);
              
              // Populate expiry date if found
              if (existingAnalysis.expiryDate) {
                setManualExpiryDate(existingAnalysis.expiryDate.toISOString().split('T')[0]);
              }
            }
          }
        } catch (error) {
          console.warn('Error checking for existing analysis:', error);
        }
      }
    };

    // Debounce the check to avoid too many API calls
    const timeoutId = setTimeout(checkExistingAnalysis, 500);
    return () => clearTimeout(timeoutId);
  }, [signerName, agreement.id, agreement.contractType, contractAnalysis]);

  const initializeFieldValues = () => {
    const values: Record<string, any> = {};
    
    // Vendor contract specific fields
    if (agreement.contractType === 'vendor_contract') {
      values['assetTypeIds'] = [];
    }
    
    setFieldValues(values);
  };

  // Reset editing state when selected completion changes
  useEffect(() => {
    setIsEditingExpiry(false);
    setEditedExpiryDate('');
  }, [selectedCompletion?.id]);

  // Compute filtered and sorted completions
  const filteredAndSortedCompletions = useMemo(() => {
    // Filter completions by location
    const filtered: AgreementCompletion[] = runsLocationFilterId === 'ALL' 
      ? completions 
      : completions.filter(c => (c.locationId || '') === runsLocationFilterId);
    
    // Sort by upload date/time in descending order (newest first)
    const sorted: AgreementCompletion[] = [...filtered].sort((a, b) => {
      const getDateValue = (completion: AgreementCompletion): number => {
        // Try signedAt first (Firestore Timestamp)
        if (completion.signedAt) {
          if (completion.signedAt?.toDate && typeof completion.signedAt.toDate === 'function') {
            return completion.signedAt.toDate().getTime();
          }
          if (completion.signedAt instanceof Date) {
            return completion.signedAt.getTime();
          }
          if (typeof completion.signedAt === 'object' && ('_seconds' in completion.signedAt || 'seconds' in completion.signedAt)) {
            const seconds = completion.signedAt._seconds || completion.signedAt.seconds || 0;
            const nanoseconds = completion.signedAt._nanoseconds || completion.signedAt.nanoseconds || 0;
            return seconds * 1000 + nanoseconds / 1000000;
          }
          if (typeof completion.signedAt === 'number') {
            return completion.signedAt;
          }
        }
        // Fallback to clientSignedAt (client-side timestamp)
        if (completion.clientSignedAt) {
          return completion.clientSignedAt;
        }
        // Fallback to 0 if no date available
        return 0;
      };
      
      const aDate = getDateValue(a);
      const bDate = getDateValue(b);
      return bDate - aDate; // Descending order (newest first)
    });
    
    return sorted;
  }, [completions, runsLocationFilterId]);

  // Capture selected completion ID for comparison (outside JSX to avoid closure issues)
  const selectedCompletionId: string | undefined = selectedCompletion?.id;

  // Filter locations when vendor changes (for vendor contracts)
  useEffect(() => {
    if (agreement.contractType === 'vendor_contract' && selectedVendorId && allLocations.length > 0 && vendors.length > 0) {
      filterLocationsByVendor(selectedVendorId, allLocations);
    } else if (!selectedVendorId && allLocations.length > 0) {
      // No vendor selected, show all locations
      setAvailableLocations(allLocations);
    }
  }, [selectedVendorId, allLocations, vendors, agreement.contractType]);

  useEffect(() => {
    // When location is selected, automatically filter recent contracts to that location
    if (selectedLocation?.id) {
      setRunsLocationFilterId(selectedLocation.id);
      // Reload asset types for vendor contracts
      if (agreement.contractType === 'vendor_contract') {
        loadAssetTypes();
      }
    }
  }, [selectedLocation?.id]);

  const loadLocations = async () => {
    // Fetch user's full data from Firestore to get locationIds
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          userRole = userData.role;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    // Load accessible locations (filtered by user's assigned locations)
    const result = await locationService.getAccessibleLocations(
      currentOrganization?.id,
      user?.id,
      userRole,
      userLocationIds
    );
    if (result.success && result.data) {
      const validLocations = result.data.filter(loc => loc.id && loc.name);
      setAllLocations(validLocations); // Store all locations
      // Filter locations based on selected vendor (if vendor is selected)
      filterLocationsByVendor(selectedVendorId, validLocations);
    }
  };

  const filterLocationsByVendor = (vendorId: string, locations: Location[]) => {
    if (!vendorId || agreement.contractType !== 'vendor_contract') {
      // No vendor selected or not a vendor contract, show all locations
      setAvailableLocations(locations);
      return;
    }

    // Find the selected vendor
    const selectedVendor = vendors.find(v => v.id === vendorId);
    if (!selectedVendor || !selectedVendor.locationIds || selectedVendor.locationIds.length === 0) {
      // Vendor has no associated locations, show all locations
      setAvailableLocations(locations);
      return;
    }

    // Filter locations to only those associated with the vendor
    const vendorLocations = locations.filter(loc => 
      loc.id && selectedVendor.locationIds?.includes(loc.id)
    );
    setAvailableLocations(vendorLocations);

    // If current selected location is not in vendor's locations, clear selection
    if (selectedLocation && selectedLocation.id && !selectedVendor.locationIds.includes(selectedLocation.id)) {
      setSelectedLocation(null);
    }
  };

  const loadVendors = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      const result = await vendorService.getAllVendors(currentOrganization.id);
      if (result.success && result.data) {
        setVendors(result.data);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadEmployees = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      const result = await employeeService.getAllEmployees(currentOrganization.id);
      if (result.success && result.data) {
        setEmployees(result.data);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadAssetTypes = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      const result = await assetService.getAllAssetTypes(currentOrganization.id);
      if (result.success && result.data) {
        setAssetTypes(result.data);
      }
    } catch (error) {
      console.error('Error loading asset types:', error);
    }
  };

  const loadHistory = async () => {
    if (!agreement.id) return;

    setIsLoadingHistory(true);
    try {
      const result = await agreementCompletionService.getCompletionsForAgreement(agreement.id);
      if (result.success && result.data) {
        setCompletions(result.data);
      }
    } catch (error) {
      console.error('Error loading agreement completion history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const validateAndSetFile = async (file: File) => {
    // Validate file size first
    try {
      const { validateFileSize } = await import('@/utils/fileSizeValidation');
      validateFileSize(file);
    } catch (error: any) {
      setFileSizeError({ fileName: file.name, fileSize: file.size });
      return false;
    }
    
    // Validate file type - allow PDF and Word documents
    const isPdf = file.type === 'application/pdf';
    const isWord = file.type === 'application/msword' || 
                   file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                   file.name.toLowerCase().endsWith('.doc') ||
                   file.name.toLowerCase().endsWith('.docx');
    
    if (!isPdf && !isWord) {
      showNotification('Please upload a PDF or Word document (.pdf, .doc, .docx)', 'error');
      return false;
    }
    // Set file first, then start analysis
    setUploadedFile(file);
    
    // Start analysis immediately after file upload
    setIsAnalyzingDocument(true);
    
    // Run analysis asynchronously
    (async () => {
      try {
        // For vendor contracts, first check if contract was already analyzed
        if (agreement.contractType === 'vendor_contract' && signerName.trim() && agreement.id) {
          const existingResult = await agreementCompletionService.getCompletionsForAgreement(agreement.id);
          if (existingResult.success && existingResult.data) {
            // Find completion with matching signerName (contract name)
            const existingCompletion = existingResult.data.find(
              c => c.signerName?.trim().toLowerCase() === signerName.trim().toLowerCase()
            );
            
            if (existingCompletion && (existingCompletion.contractSummary || existingCompletion.expiryDate)) {
              // Use existing analysis
              const getDateValue = (date: any): Date | null => {
                if (!date) return null;
                if (date instanceof Date) return date;
                if (date?.toDate && typeof date.toDate === 'function') return date.toDate();
                if (typeof date === 'string') return new Date(date);
                if (typeof date === 'number') return new Date(date);
                return null;
              };
              
              // Reconstruct ContractAnalysis from existing completion
              const existingAnalysis: ContractAnalysis = {
                summary: existingCompletion.contractSummary || '',
                contractDate: getDateValue(existingCompletion.startDate || existingCompletion.contractDate),
                expiryDate: getDateValue(existingCompletion.endDate || existingCompletion.expiryDate),
                keyInformation: existingCompletion.contractKeyInformation || {}
              };
              
              setContractAnalysis(existingAnalysis);
              
              // Populate expiry date if found
              if (existingAnalysis.expiryDate) {
                setManualExpiryDate(existingAnalysis.expiryDate.toISOString().split('T')[0]);
                showNotification('Using existing contract analysis. Expiry date found.', 'success');
              } else {
                showNotification('Using existing analysis, but expiry date not found. Please enter it manually.', 'info');
              }
              setIsAnalyzingDocument(false);
              return; // Skip re-parsing
            }
          }
        }
        
        // Analyze the document with AI (for all contract types)
        showNotification('Analyzing document...', 'info');
        const analysis = await parseContract(file);
        setContractAnalysis(analysis);
        
        // Populate expiry date if found
        if (analysis?.expiryDate) {
          setManualExpiryDate(analysis.expiryDate.toISOString().split('T')[0]);
          if (agreement.contractType === 'vendor_contract') {
            showNotification('Document analyzed. Expiry date found.', 'success');
          } else {
            showNotification('Document analyzed successfully.', 'success');
          }
        } else if (agreement.contractType === 'vendor_contract') {
          showNotification('Document analyzed. Expiry date not found - please enter it manually.', 'info');
        } else {
          showNotification('Document analyzed successfully.', 'success');
        }
      } catch (parseError: any) {
        console.warn('⚠️ Contract parsing failed:', parseError);
        setContractAnalysis(null);
        if (agreement.contractType === 'vendor_contract') {
          showNotification('Could not analyze document. Please enter expiry date manually.', 'info');
        } else {
          showNotification('Could not analyze document, but you can still proceed.', 'info');
        }
      } finally {
        setIsAnalyzingDocument(false);
      }
    })();
    
    return true;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await validateAndSetFile(file);
      // Reset input so same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      await validateAndSetFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if subscription is cancelled before uploading contract
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to upload contracts.', 'error');
      navigate('/subscription');
      return;
    }

    if (!signerName.trim()) {
      showNotification('Please enter your name', 'error');
      return;
    }

    if (!uploadedFile) {
      showNotification('Please upload a signed contract document', 'error');
      return;
    }

    if (!selectedLocation) {
      showNotification('Please select a location', 'error');
      return;
    }

    // Validate vendor selection for vendor contracts
    if (agreement.contractType === 'vendor_contract' && !selectedVendorId) {
      showNotification('Please select a vendor for vendor contracts', 'error');
      return;
    }

    // Validate employee selection for employee contracts
    if (agreement.contractType === 'employee_contract' && !selectedEmployeeId) {
      showNotification('Please select an employee for employee contracts', 'error');
      return;
    }

    // Validate Contract ID/Name (signerName) is set (mandatory field)
    if (!signerName || !signerName.trim()) {
      showNotification('Please enter a Contract ID / Name', 'error');
      return;
    }

    // For vendor contracts, validate expiry date is present
    if (agreement.contractType === 'vendor_contract') {
      const hasExpiryDate = contractAnalysis?.expiryDate || manualExpiryDate.trim();
      if (!hasExpiryDate) {
        showNotification('Expiry date is required for vendor contracts. Please enter the expiry date before uploading.', 'error');
        setIsSubmitting(false);
        setIsUploading(false);
        return;
      }
    }

    if (!agreement.id || !currentOrganization?.id) {
      showNotification('Missing required information', 'error');
      return;
    }

    setIsSubmitting(true);
    setIsUploading(true);
    try {
      // Upload the signed document
      showNotification('Uploading document...', 'info');
      const signedPdfUrl = await uploadAgreementDocument(uploadedFile, {
        docType: 'agreement_completion',
        organizationId: currentOrganization.id,
        locationId: selectedLocation.id,
        agreementId: agreement.id,
        templateId: agreement.templateId,
        uploadedBy: user
          ? { id: user.id, email: user.email, name: user.name }
          : undefined,
        title: `${agreement.name || 'Agreement'} - ${signerName || user?.name || 'Signed'}`,
        source: 'web',
        additionalMetadata: {
          signerName: signerName,
        },
      });

      // Use already parsed contract analysis (parsed on file upload)
      // Analysis should already be done by this point
      const contractAnalysisData = contractAnalysis;

      // Normalize keyInformation to ensure all values are strings (flatten nested objects)
      const normalizeKeyInformation = (keyInfo: Record<string, any> | undefined): Record<string, string> => {
        if (!keyInfo) return {};
        const normalized: Record<string, string> = {};
        for (const [key, value] of Object.entries(keyInfo)) {
          if (value === null || value === undefined) {
            normalized[key] = '';
          } else if (typeof value === 'object') {
            // If it's an object, stringify it
            try {
              normalized[key] = JSON.stringify(value);
            } catch (e) {
              normalized[key] = String(value);
            }
          } else {
            normalized[key] = String(value);
          }
        }
        return normalized;
      };

      // Get vendor name if vendor is selected
      let vendorName: string | undefined;
      if (selectedVendorId && agreement.contractType === 'vendor_contract') {
        const selectedVendor = vendors.find(v => v.id === selectedVendorId);
        vendorName = selectedVendor?.name;
      }

      // Get employee name if employee is selected
      let employeeName: string | undefined;
      if (selectedEmployeeId && agreement.contractType === 'employee_contract') {
        const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
        employeeName = selectedEmployee?.name;
      }

      // Save completion record
      const signedDate = new Date();
      
      // Extract dates from AI analysis or manual entry
      let startDate: Date | undefined;
      let endDate: Date | undefined;
      
      if (contractAnalysisData?.contractDate) {
        startDate = contractAnalysisData.contractDate;
      }
      if (contractAnalysisData?.expiryDate) {
        endDate = contractAnalysisData.expiryDate;
      }
      
      // For vendor contracts, try to get expiry date from AI or manual entry
      // If AI didn't find it, use manual entry if provided
      if (agreement.contractType === 'vendor_contract') {
        if (!endDate && manualExpiryDate) {
          const manualDate = new Date(manualExpiryDate);
          if (!isNaN(manualDate.getTime())) {
            endDate = manualDate;
          }
        }
        
        // Note: Expiry date is not required - AI will try to find it, or user can optionally enter it
        // If neither AI nor user provides it, we'll continue without it (status updates will handle it)
      }
      
      const completionData: any = {
        agreementId: agreement.id,
        agreementName: agreement.name,
        signerName: signerName.trim(),
        signerEmail: undefined,
        signatureDataUrl: '', // Empty string instead of undefined
        signedPdfUrl: signedPdfUrl,
        organizationId: currentOrganization.id,
        locationId: selectedLocation.id,
        signedDate: signedDate,
        // Include parsed contract data if available
        contractSummary: contractAnalysisData?.summary,
        contractDate: startDate ? startDate.toISOString() : undefined,
        expiryDate: endDate ? endDate.toISOString() : undefined,
        contractKeyInformation: normalizeKeyInformation(contractAnalysisData?.keyInformation),
        // Store dates in both places for compatibility
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
        // Set initial status (will be updated by background job)
        status: 'Active',
      };

      // Add vendor fields if this is a vendor contract
      if (agreement.contractType === 'vendor_contract' && selectedVendorId) {
        completionData.vendorId = selectedVendorId;
        completionData.vendorName = vendorName;
      }

      // Add employee fields if this is an employee contract
      if (agreement.contractType === 'employee_contract' && selectedEmployeeId) {
        completionData.employeeId = selectedEmployeeId;
        completionData.employeeName = employeeName;
      }

      // Add fixed field values (always include for all contracts)
      completionData.fieldValues = fieldValues;
      
      console.log('💾 Saving completion with data:', {
        hasContractSummary: !!completionData.contractSummary,
        hasContractDate: !!completionData.contractDate,
        hasExpiryDate: !!completionData.expiryDate,
        hasKeyInformation: !!completionData.contractKeyInformation,
        contractSummary: completionData.contractSummary?.substring(0, 100),
        expiryDate: completionData.expiryDate,
      });
      
      const result = await agreementCompletionService.addCompletion(completionData);

      if (result.success) {
        // Track contract upload
        trackEvidence.uploaded('contract', uploadedFile.size);
        
        showNotification('Contract uploaded successfully', 'success');
        // Reset form
        setSignerName('');
        setSelectedVendorId('');
        setSelectedEmployeeId('');
        setUploadedFile(null);
        setManualExpiryDate('');
        setContractAnalysis(null);
        initializeFieldValues();
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        // Reload history and select the newly uploaded contract
        await loadHistory();
        // Find and select the newly uploaded contract by ID
        if (result.id) {
          const historyResult = await agreementCompletionService.getCompletionsForAgreement(agreement.id!);
          if (historyResult.success && historyResult.data) {
            const newContract = historyResult.data.find(c => c.id === result.id);
            if (newContract) {
              setSelectedCompletion(newContract);
            }
          }
        }
        // Don't call onComplete() - keep the window open for review
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error uploading contract:', error);
      showNotification(`Failed to upload contract: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  const formatDate = (completion: AgreementCompletion): string => {
    const dateValue = completion.signedAt;
    
    // Debug: log the actual data structure
    if (dateValue && typeof dateValue === 'object') {
      console.log('📅 Date value structure:', {
        hasToDate: 'toDate' in dateValue,
        hasSeconds: '_seconds' in dateValue || 'seconds' in dateValue,
        keys: Object.keys(dateValue),
        _methodName: dateValue._methodName,
        _seconds: dateValue._seconds,
        seconds: dateValue.seconds,
        clientSignedAt: completion.clientSignedAt
      });
    }
    
    if (!dateValue) {
      // If no signedAt, try clientSignedAt
      if (completion.clientSignedAt) {
        const date = new Date(completion.clientSignedAt);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      }
      return 'N/A';
    }
    
    try {
      let date: Date | null = null;
      
      // PRIORITY 1: Check if it's a Firestore Timestamp instance
      if (dateValue instanceof Timestamp) {
        try {
          date = dateValue.toDate();
          if (date && !isNaN(date.getTime())) {
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          }
        } catch (e) {
          console.warn('Timestamp.toDate() failed:', e);
        }
      }
      
      // PRIORITY 2: Handle Firestore Timestamp with toDate method (most common after Firestore resolves)
      if (!date && dateValue && typeof dateValue === 'object' && 'toDate' in dateValue && typeof dateValue.toDate === 'function') {
        try {
          date = dateValue.toDate();
          if (date && !isNaN(date.getTime())) {
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          }
        } catch (e) {
          console.warn('toDate() failed, trying alternative:', e);
        }
      }
      
      // PRIORITY 3: Handle Firestore Timestamp with _seconds property (alternative format)
      if (!date && dateValue && typeof dateValue === 'object' && ('_seconds' in dateValue || 'seconds' in dateValue)) {
        const seconds = dateValue._seconds || dateValue.seconds || 0;
        const nanoseconds = dateValue._nanoseconds || dateValue.nanoseconds || 0;
        if (seconds > 0) {
          date = new Date(seconds * 1000 + nanoseconds / 1000000);
          if (date && !isNaN(date.getTime())) {
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          }
        }
      }
      
      // PRIORITY 3: Handle Date object
      if (!date && dateValue instanceof Date) {
        date = dateValue;
      }
      
      // PRIORITY 4: Handle number (timestamp in milliseconds or seconds)
      if (!date && typeof dateValue === 'number') {
        // If number is less than a certain threshold, assume it's in seconds
        date = dateValue < 1000000000000 ? new Date(dateValue * 1000) : new Date(dateValue);
      }
      
      // PRIORITY 5: Handle string
      if (!date && typeof dateValue === 'string') {
        date = new Date(dateValue);
      }
      
      // PRIORITY 6: Handle serverTimestamp placeholder (only if not yet resolved)
      if (!date && dateValue && typeof dateValue === 'object' && dateValue._methodName === 'serverTimestamp') {
        // This is a placeholder, use clientSignedAt if available
        if (completion.clientSignedAt) {
          date = new Date(completion.clientSignedAt);
        } else {
          console.warn('serverTimestamp placeholder with no clientSignedAt - cannot determine actual date');
          return 'Date pending';
        }
      }
      
      // Validate the date
      if (date && !isNaN(date.getTime())) {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      // Final fallback: use clientSignedAt if available
      if (completion.clientSignedAt) {
        const fallbackDate = new Date(completion.clientSignedAt);
        if (!isNaN(fallbackDate.getTime())) {
          return fallbackDate.toLocaleDateString() + ' ' + fallbackDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      }
      
      console.error('Could not parse date:', dateValue, 'Type:', typeof dateValue, 'Keys:', dateValue && typeof dateValue === 'object' ? Object.keys(dateValue) : 'N/A');
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error, 'Date value:', dateValue);
      // Try clientSignedAt as last resort
      if (completion.clientSignedAt) {
        const fallbackDate = new Date(completion.clientSignedAt);
        if (!isNaN(fallbackDate.getTime())) {
          return fallbackDate.toLocaleDateString() + ' ' + fallbackDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      }
      return 'Invalid date';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130] p-4" style={{ height: '95vh', display: 'flex', flexDirection: 'column' }}>
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] flex flex-col"
        style={{ height: '95vh', maxHeight: '95vh', minHeight: 0 }}
      >
        {/* Header */}
        <div 
          className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0"
        >
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {viewMode === 'history' ? 'Contract History' : 'Upload Contract/Agreement'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{agreement.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content - Single Column Layout */}
        <div className="flex-1 flex overflow-hidden min-h-0" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
          {/* Upload Form - Hidden in history mode */}
          {viewMode === 'upload' && (
          <div className="flex-1 overflow-y-auto bg-white border-r border-gray-200" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: '1 1 50%' }}>
              <form onSubmit={handleSubmit} className="p-3 space-y-3">
                {/* Contract ID/Name - Required field, user must enter (First field) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Contract ID / Name <span className="text-red-600">*</span>{' '}
                    <span className="text-gray-500 font-normal">(e.g., "Boiler Maintenance 2024–2025")</span>
                  </label>
                  <input
                    type="text"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter contract ID or name"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Vendor Selection - Only show for vendor contracts (comes before location) */}
                {agreement.contractType === 'vendor_contract' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Vendor <span className="text-red-600">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={selectedVendorId}
                        onChange={(e) => setSelectedVendorId(e.target.value)}
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        <option value="">Select Vendor...</option>
                        {vendors.map(vendor => (
                          <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddVendorForm(true)}
                        className="px-3 py-1.5 text-xs font-medium text-purple-600 border border-purple-300 rounded-md hover:bg-purple-50 transition-colors flex items-center gap-1"
                        title="Add New Vendor"
                      >
                        <Plus className="w-3 h-3" />
                        Add Vendor
                      </button>
                    </div>
                  </div>
                )}

                {/* Location Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Location <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={selectedLocation?.id || ''}
                    onChange={(e) => {
                      const location = availableLocations.find(loc => loc.id === e.target.value);
                      setSelectedLocation(location || null);
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                    disabled={agreement.contractType === 'vendor_contract' && !selectedVendorId}
                  >
                    <option value="">
                      {agreement.contractType === 'vendor_contract' && !selectedVendorId 
                        ? 'Select vendor first...' 
                        : 'Select location...'}
                    </option>
                    {availableLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                  {agreement.contractType === 'vendor_contract' && !selectedVendorId && (
                    <p className="text-xs text-gray-500 mt-1">Please select a vendor first to see associated locations</p>
                  )}
                </div>

                {/* Employee Selection - Only show for employee contracts */}
                {agreement.contractType === 'employee_contract' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Employee <span className="text-red-600">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={selectedEmployeeId}
                        onChange={(e) => setSelectedEmployeeId(e.target.value)}
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        <option value="">Select Employee...</option>
                        {employees.map(employee => (
                          <option key={employee.id} value={employee.id}>{employee.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddEmployeeForm(true)}
                        className="px-3 py-1.5 text-xs font-medium text-purple-600 border border-purple-300 rounded-md hover:bg-purple-50 transition-colors flex items-center gap-1"
                        title="Add New Employee"
                      >
                        <Plus className="w-3 h-3" />
                        Add Employee
                      </button>
                    </div>
                  </div>
                )}

                {/* Vendor Contract Specific Fields */}
                {agreement.contractType === 'vendor_contract' && (
                  <div className="border-t border-gray-200 pt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Contract Details
                    </label>
                    <div className="space-y-3">
                      {/* Expiry Date - Only show if AI couldn't find it (for vendor contracts) */}
                      {agreement.contractType === 'vendor_contract' && 
                       contractAnalysis && 
                       !contractAnalysis.expiryDate && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Expiry Date <span className="text-red-600">*</span>
                            <span className="text-red-600 text-xs font-normal ml-2">
                              Required - AI could not find it
                            </span>
                          </label>
                          <div className="space-y-1">
                            <input
                              type="date"
                              value={manualExpiryDate}
                              onChange={(e) => setManualExpiryDate(e.target.value)}
                              className={`w-full px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                !manualExpiryDate
                                  ? 'border-red-300 bg-red-50'
                                  : 'border-gray-300'
                              }`}
                              required
                              min={new Date().toISOString().split('T')[0]}
                            />
                            <p className="text-xs text-red-600">
                              AI could not find the expiry date. Please enter it manually before uploading.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Asset Type Covered (Multi-select) */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Asset Type Covered
                        </label>
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2">
                          {assetTypes.length === 0 ? (
                            <p className="text-xs text-gray-500">No asset types available</p>
                          ) : (
                            assetTypes.map(assetType => (
                              <label key={assetType.id} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(fieldValues['assetTypeIds'] as string[] || []).includes(assetType.id || '')}
                                  onChange={(e) => {
                                    const current = (fieldValues['assetTypeIds'] as string[] || []);
                                    if (e.target.checked) {
                                      setFieldValues({ ...fieldValues, assetTypeIds: [...current, assetType.id] });
                                    } else {
                                      setFieldValues({ ...fieldValues, assetTypeIds: current.filter(id => id !== assetType.id) });
                                    }
                                  }}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{assetType.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Document Upload */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Upload Signed Contract <span className="text-red-600">*</span>
                  </label>
                  <label 
                    htmlFor="contract-upload"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-md cursor-pointer transition-colors ${
                      isDragging 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center">
                      {isAnalyzingDocument || isUploading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600 mb-1"></div>
                      ) : (
                        <Upload className="w-5 h-5 text-gray-500 mb-1" />
                      )}
                      <p className="text-xs text-gray-600">
                        {isAnalyzingDocument ? 'Analyzing document...' : isUploading ? 'Uploading...' : uploadedFile ? uploadedFile.name : 'Click to upload or drag and drop'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">PDF or Word document</p>
                    </div>
                  <input
                      id="contract-upload"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                  {uploadedFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md text-xs">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-green-600" />
                          <span className="text-gray-700">{uploadedFile.name}</span>
                          <span className="text-gray-500">
                            ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedFile(null);
                            setContractAnalysis(null);
                            setManualExpiryDate('');
                            setIsAnalyzingDocument(false);
                            const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                            if (fileInput) fileInput.value = '';
                          }}
                          className="text-red-600 hover:text-red-800"
                          disabled={isAnalyzingDocument}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                    disabled={isSubmitting || isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                    disabled={
                      isSubmitting || 
                      isUploading || 
                      isAnalyzingDocument || 
                      !uploadedFile || 
                      !signerName.trim() ||
                      (agreement.contractType === 'vendor_contract' && !contractAnalysis?.expiryDate && !manualExpiryDate.trim())
                    }
                  >
                    {isAnalyzingDocument ? 'Analyzing document...' : isSubmitting || isUploading ? 'Uploading...' : 'Upload Contract'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Right Column - Recent Contracts */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0 bg-white" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: viewMode === 'history' ? '1 1 100%' : '1 1 50%' }}>
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-gray-800">
                  {selectedCompletion ? 'Contract Details' : 'Recent Contracts'}
                </h4>
                {selectedCompletion && (
                  <button
                    onClick={() => setSelectedCompletion(null)}
                    className="text-gray-500 hover:text-gray-700 p-1"
                    title="Back to list"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
          </div>
              {!selectedCompletion && (
                <div className="flex items-center gap-2">
                  {/* Location filter */}
                  <select
                    value={runsLocationFilterId}
                    onChange={(e) => {
                      const newFilterId = e.target.value;
                      setRunsLocationFilterId(newFilterId);
                      // Also update the location selection on the left side
                      if (newFilterId !== 'ALL') {
                        const location = availableLocations.find(loc => loc.id === newFilterId);
                        if (location) {
                          setSelectedLocation(location);
                        }
                      }
                    }}
                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                    title="Filter by location"
                  >
                    <option value="ALL">All Locations</option>
                    {availableLocations.map((location) => (
                      <option key={location.id} value={location.id || ''}>{location.name}</option>
                    ))}
                  </select>
                  {isLoadingHistory && (
                    <span className="text-xs text-gray-500">Loading...</span>
                  )}
                </div>
              )}
            </div>
            <div 
              className="flex-1 overflow-y-auto min-h-0"
              style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}
            >
              {selectedCompletion ? (
                // Detail View
                <div className="p-3 space-y-3">
                  {/* Basic Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium text-gray-700">Contract Name:</span>{' '}
                        <span className="text-gray-900">{selectedCompletion.agreementName || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Signer Name:</span>{' '}
                        <span className="text-gray-900">{selectedCompletion.signerName || 'Unknown'}</span>
                      </div>
                      {selectedCompletion.vendorName && (
                        <div>
                          <span className="font-medium text-gray-700">Vendor:</span>{' '}
                          <span className="text-gray-900">{selectedCompletion.vendorName}</span>
                        </div>
                      )}
                      {selectedCompletion.employeeName && (
                        <div>
                          <span className="font-medium text-gray-700">Employee:</span>{' '}
                          <span className="text-gray-900">{selectedCompletion.employeeName}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-700">Signed Date:</span>{' '}
                        <span className="text-gray-900">{formatDate(selectedCompletion)}</span>
                      </div>
                      {selectedCompletion.signedPdfUrl && (
                        <div className="mt-2">
                          <a
                            href={selectedCompletion.signedPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-purple-600 hover:text-purple-800 underline flex items-center gap-1"
                          >
                            <FileText className="w-3 h-3" />
                            View Signed PDF
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI-Parsed Contract Information */}
                  {selectedCompletion.contractSummary || selectedCompletion.contractDate || selectedCompletion.expiryDate || selectedCompletion.contractKeyInformation ? (
                    <div className="bg-purple-50 border border-purple-200 rounded p-3">
                      <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        AI Analysis Summary
                      </h4>
                      <div className="space-y-3 text-xs">
                        {selectedCompletion.contractSummary && (
                          <div>
                            <span className="font-medium text-gray-700 block mb-1">Complete Summary:</span>
                            <p className="text-gray-900 leading-relaxed">{selectedCompletion.contractSummary}</p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-3">
                          {selectedCompletion.contractDate && (
                            <div className="bg-white border border-gray-200 rounded p-2">
                              <div className="flex items-center gap-1 mb-1">
                                <Calendar className="w-3 h-3 text-gray-500" />
                                <span className="font-medium text-gray-700">Contract Date</span>
                              </div>
                              <p className="text-gray-900 font-semibold">
                                {(() => {
                                  const date = typeof selectedCompletion.contractDate === 'string' 
                                    ? new Date(selectedCompletion.contractDate)
                                    : selectedCompletion.contractDate?.toDate?.() || selectedCompletion.contractDate;
                                  return date instanceof Date && !isNaN(date.getTime()) 
                                    ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                    : 'Not available';
                                })()}
                              </p>
                            </div>
                          )}
                          <div className="bg-white border border-gray-200 rounded p-2">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-gray-500" />
                                <span className="font-medium text-gray-700">Expiry Date</span>
                              </div>
                              {!isEditingExpiry && (
                                <button
                                  onClick={() => {
                                    // Initialize edited date with current expiry date or today
                                    const currentExpiry = selectedCompletion.expiryDate
                                      ? (typeof selectedCompletion.expiryDate === 'string'
                                        ? new Date(selectedCompletion.expiryDate)
                                        : selectedCompletion.expiryDate?.toDate?.() || selectedCompletion.expiryDate)
                                      : null;
                                    const dateToUse = currentExpiry instanceof Date && !isNaN(currentExpiry.getTime())
                                      ? currentExpiry
                                      : new Date();
                                    setEditedExpiryDate(dateToUse.toISOString().split('T')[0]);
                                    setIsEditingExpiry(true);
                                  }}
                                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                                  title="Edit expiry date"
                                >
                                  <Pen className="w-3 h-3" />
                                  {selectedCompletion.expiryDate ? 'Edit' : 'Set'}
                                </button>
                              )}
                            </div>
                            {isEditingExpiry ? (
                              <div className="space-y-2">
                                <input
                                  type="date"
                                  value={editedExpiryDate}
                                  onChange={(e) => setEditedExpiryDate(e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={async () => {
                                      if (!selectedCompletion.id || !editedExpiryDate) return;
                                      setIsUpdatingExpiry(true);
                                      try {
                                        const result = await agreementCompletionService.updateExpiryDate(
                                          selectedCompletion.id,
                                          editedExpiryDate
                                        );
                                        if (result.success) {
                                          showNotification('Expiry date updated successfully', 'success');
                                          // Update local state
                                          const updatedCompletion = {
                                            ...selectedCompletion,
                                            expiryDate: editedExpiryDate,
                                          };
                                          setSelectedCompletion(updatedCompletion);
                                          // Update in completions list
                                          setCompletions(prev =>
                                            prev.map(c => c.id === selectedCompletion.id ? updatedCompletion : c)
                                          );
                                          setIsEditingExpiry(false);
                                          // Reload history to ensure consistency
                                          await loadHistory();
                                          // Re-select the updated completion
                                          const historyResult = await agreementCompletionService.getCompletionsForAgreement(agreement.id!);
                                          if (historyResult.success && historyResult.data) {
                                            const updated = historyResult.data.find(c => c.id === selectedCompletion.id);
                                            if (updated) {
                                              setSelectedCompletion(updated);
                                            }
                                          }
                                        } else {
                                          showNotification(`Error: ${result.error}`, 'error');
                                        }
                                      } catch (error: any) {
                                        showNotification(`Failed to update expiry date: ${error.message}`, 'error');
                                      } finally {
                                        setIsUpdatingExpiry(false);
                                      }
                                    }}
                                    disabled={isUpdatingExpiry || !editedExpiryDate}
                                    className="px-2 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setIsEditingExpiry(false);
                                      setEditedExpiryDate('');
                                    }}
                                    disabled={isUpdatingExpiry}
                                    className="px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className={`font-semibold ${
                                (() => {
                                  if (!selectedCompletion.expiryDate) return 'text-gray-500';
                                  const expiry = typeof selectedCompletion.expiryDate === 'string' 
                                    ? new Date(selectedCompletion.expiryDate)
                                    : selectedCompletion.expiryDate?.toDate?.() || selectedCompletion.expiryDate;
                                  if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'text-gray-500';
                                  const now = new Date();
                                  const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                  if (daysUntilExpiry < 0) return 'text-red-600';
                                  if (daysUntilExpiry < 30) return 'text-orange-600';
                                  return 'text-green-600';
                                })()
                              }`}>
                                {(() => {
                                  if (!selectedCompletion.expiryDate) return 'Not set - Click Edit to set';
                                  const expiry = typeof selectedCompletion.expiryDate === 'string' 
                                    ? new Date(selectedCompletion.expiryDate)
                                    : selectedCompletion.expiryDate?.toDate?.() || selectedCompletion.expiryDate;
                                  if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'Not available';
                                  const formatted = expiry.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                                  const now = new Date();
                                  const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                  if (daysUntilExpiry < 0) return `${formatted} (Expired ${Math.abs(daysUntilExpiry)} days ago)`;
                                  if (daysUntilExpiry < 30) return `${formatted} (Expires in ${daysUntilExpiry} days)`;
                                  return formatted;
                                })()}
                              </p>
                            )}
                          </div>
                        </div>

                        {selectedCompletion.contractKeyInformation && Object.keys(selectedCompletion.contractKeyInformation).length > 0 && (
                          <div className="bg-white border border-gray-200 rounded p-2">
                            <span className="font-medium text-gray-700 block mb-2">Key Information Extracted:</span>
                            <div className="space-y-1.5">
                              {Object.entries(selectedCompletion.contractKeyInformation).map(([key, value]) => {
                                // Convert value to string if it's an object
                                let displayValue: string;
                                if (value === null || value === undefined) {
                                  displayValue = 'N/A';
                                } else if (typeof value === 'object') {
                                  // If it's an object, stringify it nicely
                                  try {
                                    displayValue = JSON.stringify(value, null, 2);
                                  } catch (e) {
                                    displayValue = String(value);
                                  }
                                } else {
                                  displayValue = String(value);
                                }
                                
                                return (
                                  <div key={key} className="border-b border-gray-100 pb-1.5 last:border-0 last:pb-0">
                                    <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                      <span className="text-gray-600 font-medium capitalize break-words min-w-0 sm:min-w-[140px] sm:flex-shrink-0">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                      <span className="text-gray-900 flex-1 whitespace-pre-wrap break-words min-w-0">{displayValue}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-800">
                      <p>No AI analysis available for this contract. The contract may have been uploaded before AI parsing was enabled, or the analysis failed.</p>
                    </div>
                  )}
                </div>
              ) : (
                // List View
                <>
                  {isLoadingHistory ? (
                    <div className="p-2 text-center text-xs text-gray-500">Loading...</div>
                  ) : completions.length === 0 ? (
                    <div className="p-2 text-center text-xs text-gray-500">No recent contracts</div>
                  ) : filteredAndSortedCompletions.length === 0 ? (
                    <div className="p-2 text-center text-xs text-gray-500">No recent contracts{selectedLocation ? ' for this location' : ''}</div>
                  ) : (
                    <div className="p-1.5 space-y-1.5">
                  {filteredAndSortedCompletions.map((completion: AgreementCompletion) => {
                    const isSelected = selectedCompletionId !== undefined && 
                                      completion.id !== undefined && 
                                      selectedCompletionId === completion.id;
                    
                    return (
                    <div
                      key={completion.id || `completion-${Math.random()}`}
                      onClick={() => setSelectedCompletion(completion)}
                          className={`p-2 border rounded-md cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                          <div className="text-xs font-medium text-gray-900">
                            {completion.signerName}
                            {(completion.contractSummary || completion.contractDate || completion.expiryDate || completion.contractKeyInformation) && (
                              <span className="text-purple-600 italic ml-1">(Click here for AI analysis)</span>
                            )}
                          </div>
                          {completion.vendorName && (
                            <div className="text-xs text-gray-600 mt-0.5">
                              Vendor: <span className="font-medium">{completion.vendorName}</span>
                            </div>
                          )}
                          {completion.employeeName && (
                            <div className="text-xs text-gray-600 mt-0.5">
                              Employee: <span className="font-medium">{completion.employeeName}</span>
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-0.5">
                        {formatDate(completion)}
                      </div>
                          {completion.contractSummary && (
                            <div className="text-xs text-gray-700 mt-1 line-clamp-2">
                              {completion.contractSummary.substring(0, 80)}...
                            </div>
                          )}
                          {completion.expiryDate && (
                            <div className="text-xs mt-1">
                              <span className="text-gray-600">Expires: </span>
                              <span className={`font-semibold ${
                                (() => {
                                  const expiry = typeof completion.expiryDate === 'string' 
                                    ? new Date(completion.expiryDate)
                                    : completion.expiryDate?.toDate?.() || completion.expiryDate;
                                  if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'text-gray-900';
                                  const now = new Date();
                                  const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                  if (daysUntilExpiry < 0) return 'text-red-600';
                                  if (daysUntilExpiry < 30) return 'text-orange-600';
                                  return 'text-green-600';
                                })()
                              }`}>
                                {(() => {
                                  const expiry = typeof completion.expiryDate === 'string' 
                                    ? new Date(completion.expiryDate)
                                    : completion.expiryDate?.toDate?.() || completion.expiryDate;
                                  if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'N/A';
                                  return expiry.toLocaleDateString();
                                })()}
                              </span>
                            </div>
                          )}
                      {completion.signedPdfUrl && (
                            <div className="mt-1">
                          <a
                            href={completion.signedPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-purple-600 hover:text-purple-800 underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                                View PDF
                          </a>
                        </div>
                      )}
                        </div>
                    );
                  })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Form Modal */}
      {showAddVendorForm && (
        <VendorForm
          onSubmit={async (vendorData: Partial<Vendor>) => {
            if (!currentOrganization?.id) {
              showNotification('Organization ID is required', 'error');
              return;
            }
            try {
              // Validate required fields
              if (!vendorData.name || !vendorData.name.trim()) {
                showNotification('Vendor name is required', 'error');
                return;
              }
              const result = await vendorService.addVendor(vendorData as Omit<Vendor, 'id'>, currentOrganization.id);
              if (result.success) {
                showNotification('Vendor added successfully', 'success');
                setShowAddVendorForm(false);
                // Reload vendors and select the new one
                await loadVendors();
                // Wait a bit for vendors to reload, then select the new vendor
                setTimeout(() => {
                  if (result.id) {
                    setSelectedVendorId(result.id);
                  }
                }, 500);
              } else {
                showNotification(`Error: ${result.error}`, 'error');
              }
            } catch (error: any) {
              console.error('Error adding vendor:', error);
              showNotification('Failed to add vendor', 'error');
            }
          }}
          onCancel={() => setShowAddVendorForm(false)}
        />
      )}

      {/* Employee Form Modal */}
      {showAddEmployeeForm && (
        <EmployeeForm
          onSubmit={async (employeeData: Partial<Employee>) => {
            if (!currentOrganization?.id) {
              showNotification('Organization ID is required', 'error');
              return;
            }
            try {
              // Validate required fields
              if (!employeeData.name || !employeeData.name.trim()) {
                showNotification('Employee name is required', 'error');
                return;
              }
              const result = await employeeService.addEmployee(employeeData as Omit<Employee, 'id'>, currentOrganization.id);
              if (result.success) {
                showNotification('Employee added successfully', 'success');
                setShowAddEmployeeForm(false);
                // Reload employees and select the new one
                await loadEmployees();
                // Wait a bit for employees to reload, then select the new employee
                setTimeout(() => {
                  if (result.id) {
                    setSelectedEmployeeId(result.id);
                  }
                }, 500);
              } else {
                showNotification(`Error: ${result.error}`, 'error');
              }
            } catch (error: any) {
              console.error('Error adding employee:', error);
              showNotification('Failed to add employee', 'error');
            }
          }}
          onCancel={() => setShowAddEmployeeForm(false)}
        />
      )}

      {/* File Size Error Modal */}
      <FileSizeErrorModal
        fileName={fileSizeError?.fileName || ''}
        fileSize={fileSizeError?.fileSize || 0}
        isOpen={!!fileSizeError}
        onClose={() => setFileSizeError(null)}
      />
    </div>
  );
};

export default RunAgreement;

